import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Typography, Button, Box, Snackbar } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { SortableTable } from 'common/components/SortableTable';
import { getUsers } from './actions/getUsers';
import { getAdminCandidates } from './actions/getAdminCandidates';
import { resetPassword } from './actions/resetPassword';
import { resendInvite } from './actions/resendInvite';
import EditUserModal from './EditUserModal';

// Items 1 + 4 — admin Users panel. Table is a real read (GET /users). Row actions are now
// live: Edit -> PUT /admin/user/:id; Reset Password -> POST /auth/resetPassword; Resend
// Invite -> POST /candidate/invitation (candidateId resolved by email from /admin/candidates,
// since GET /users carries no candidateId — disabled when a user has no candidate profile).
const TABLE_HEADERS = [
  { id: 'id', align: 'left', numeric: true, label: 'ID' },
  { id: 'name', align: 'left', numeric: false, label: 'NAME' },
  { id: 'email', align: 'left', numeric: false, label: 'EMAIL' },
  { id: 'actions', align: 'right', numeric: false, label: '' },
];

const fullName = (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim();

function createData(user, handlers, candidateId) {
  return [
    { id: 'id', value: user.id },
    { id: 'name', value: fullName(user) },
    { id: 'email', value: user.email },
    {
      id: 'actions',
      align: 'right',
      value: (
        <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
          <Button size="small" variant="outlined" onClick={() => handlers.onEdit(user)}>
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!candidateId}
            title={candidateId ? '' : 'No candidate profile for this user'}
            onClick={() => handlers.onInvite(user, candidateId)}
          >
            Resend Invite
          </Button>
          <Button size="small" variant="outlined" onClick={() => handlers.onReset(user)}>
            Reset Password
          </Button>
        </Box>
      ),
    },
  ];
}

const Users = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [toast, setToast] = useState({ msg: '', severity: 'success' });

  const notify = (msg, severity = 'success') => setToast({ msg, severity });

  const handlers = {
    onEdit: (user) => {
      setEditing(user);
      setEditOpen(true);
    },
    onInvite: async (user, candidateId) => {
      if (!candidateId) {
        notify(`No candidate profile found for ${user.email}.`, 'warning');
        return;
      }
      const result = await resendInvite(candidateId);
      if (result.ok) notify(`Invitation re-sent to ${user.email}.`);
      else notify(result.message, 'error');
    },
    onReset: async (user) => {
      const result = await resetPassword(user.email);
      if (result.ok) notify(`Password reset link sent to ${user.email}.`);
      else notify(result.message, 'error');
    },
  };

  const load = useCallback(() => {
    let mounted = true;
    Promise.all([getUsers(), getAdminCandidates()]).then(([users, candidates]) => {
      if (!mounted) return;
      // Resolve candidateId by email. If an email maps to more than one candidate it is
      // ambiguous, so we leave Resend Invite disabled for that user.
      const counts = {};
      const byEmail = {};
      candidates.forEach((c) => {
        const key = (c.email || '').toLowerCase();
        if (!key) return;
        counts[key] = (counts[key] || 0) + 1;
        byEmail[key] = c.id;
      });
      setRows(
        users.map((u) => {
          const key = (u.email || '').toLowerCase();
          const candidateId = counts[key] === 1 ? byEmail[key] : null;
          return createData(u, handlers, candidateId);
        }),
      );
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => load(), [load]);

  return (
    <Grid item xs={12}>
      <Typography variant="h5" style={{ marginTop: 16, marginBottom: 8 }}>
        Users
      </Typography>
      <SortableTable
        name="admin-users"
        headers={TABLE_HEADERS}
        rows={rows}
        defaultSortColumnIndex={1}
        searchable
        searchColumnName="name"
      />
      <EditUserModal
        open={editOpen}
        user={editing}
        onClose={() => setEditOpen(false)}
        onSaved={(msg) => {
          notify(msg);
          load();
        }}
      />
      <Snackbar
        open={!!toast.msg}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, msg: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={toast.severity}
          onClose={() => setToast({ ...toast, msg: '' })}
        >
          {toast.msg}
        </MuiAlert>
      </Snackbar>
    </Grid>
  );
};

export default Users;
